//  Angular imports
import { Component, OnInit, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
//  Application imports
import { ApplicationRoute } from 'app/models/application-route';
import { HKeyManagerService } from 'app/services/hkey-manager.service';
import { RAccount } from 'app/records/account.record';
import { RAccountDetails } from 'app/records/account-details.record';
import { RAccountInformationDetails } from 'app/records/account-information-details-records';
import { RHKeyData } from 'app/records/h-key-data.record';
import { ROrganization } from 'app/records/organization-record';
import { UserService } from 'app/services/user.service';
import { NgIf, NgFor } from '@angular/common';
import { LoadingIndicatorComponent } from '../loading-indicator/loading-indicator.component';
import { SidebarComponent } from '../sidebar/sidebar.component';
import { OrgChooserComponent } from '../organization-chooser/org-chooser.component';

@Component({
    selector: 'zf-account-selection',
    templateUrl: './account-selection.component.html',
    styleUrls: ['./account-selection.component.scss'],
    imports: [NgIf, LoadingIndicatorComponent, SidebarComponent, NgFor, OrgChooserComponent]
})
export class AccountSelectionComponent implements OnInit {
    private router = inject(Router);
    private userService = inject(UserService);
    private hKeyManagerService = inject(HKeyManagerService);

    public readonly showPartialCreatedAccountInfo = signal<boolean>(false);
    public readonly accounts = signal<Array<RAccountDetails>>(undefined);
    public readonly hasOrgs = signal<boolean>(false);
    public readonly orgNodes = signal<Array<ROrganization>>(undefined);

    public ngOnInit(): void {
        const account: RAccount = this.userService.accountSelected();
        if (account?.details) {
            this.setSelectedAccount(account);
        } else {
            this.loadAccounts();
        }
    }

    public pickAccount(accountDetails: RAccountDetails): void {
        this.accounts.set(null);
        this.userService.getAccount(accountDetails.accountId, accountDetails.userId)
            .subscribe(
                (account: RAccount): void => this.setSelectedAccount(account)
            );
    }

    private loadAccounts(defaultAccount?: RAccount): void {
        this.userService.getAccounts()
            .subscribe({
                next: (accountInfo: RAccountInformationDetails): void => {
                    if (accountInfo.hasOrg) {
                        this.hasOrgs.set(true);
                        this.accounts.set(accountInfo.accounts);
                        this.orgNodes.set(accountInfo.organizations);
                        this.userService.setOrganizations(accountInfo.organizations);
                    }
                    else {
                        if (!!accountInfo && accountInfo.accounts.length === 0) {
                            this.showPartialCreatedAccountInfo.set(true);
                        } else if (!!accountInfo && accountInfo.accounts.length === 1) {
                            if (!defaultAccount || accountInfo.accounts[0].accountId !== defaultAccount.details.accountId) {
                                this.pickAccount(accountInfo.accounts[0]);
                            } else {
                                this.setSelectedAccount(defaultAccount);
                            }
                        } else {
                            this.accounts.set(
                                [...accountInfo.accounts].sort((a: RAccountDetails, b: RAccountDetails) =>
                                    Number(b.isMasterUser) - Number(a.isMasterUser)
                                )
                            );
                        }
                    }
                },
                error: (err: unknown): void => console.error('Unable to retrieve accounts', err)
            });
    }

    private completeAndStore(account: RAccountDetails): void {
        const hKeyDataPartial: Partial<RHKeyData> = RHKeyData.partialFromAccount(account);
        const storedHKey: RHKeyData = this.hKeyManagerService.get();
        const hKeyData: RHKeyData = RHKeyData.complete(hKeyDataPartial, storedHKey);
        this.hKeyManagerService.store(hKeyData);
    }

    private setSelectedAccount(account: RAccount): void {
        this.completeAndStore(account.details);
        this.userService.selectAccount(account);
        this.router.navigateByUrl(ApplicationRoute.ENTITLEMENTS);
    }
}
